import { useEffect, useState } from "react";
import { Copy, Trash2, UserPlus } from "lucide-react";
import { staff as staffApi, type StaffMember, type StaffInviteRow } from "../lib/api";

const ROLE_LABEL: Record<string, string> = {
  owner: "Owner", manager: "Manager", analyst: "Analyst", viewer: "Viewer",
};
const INVITE_ROLES = [
  { value: "analyst", label: "Analyst — works clients (no billing/settings)" },
  { value: "manager", label: "Manager — full client work + billing" },
  { value: "viewer", label: "Viewer — read-only" },
];

export function StaffManager({ currentUserId }: { currentUserId?: string }) {
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [invites, setInvites] = useState<StaffInviteRow[]>([]);
  const [cap, setCap] = useState(5);
  const [slotsLeft, setSlotsLeft] = useState(5);
  const [loading, setLoading] = useState(true);

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("analyst");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [emailedTo, setEmailedTo] = useState<string | null>(null);

  async function refresh() {
    const r = await staffApi.list();
    setStaff(r.staff); setInvites(r.pending_invites);
    setCap(r.cap); setSlotsLeft(r.slots_left);
    setLoading(false);
  }
  useEffect(() => { refresh().catch((e: any) => { setError(e?.message || "Could not load staff"); setLoading(false); }); }, []);

  async function invite(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null); setInviteLink(null); setEmailedTo(null);
    try {
      const r = await staffApi.invite(email.trim().toLowerCase(), role, name.trim() || undefined);
      // If Resend isn't configured the invite still exists — surface the link to copy.
      if (r.email.mode !== "resend") setInviteLink(r.invite_url);
      else setEmailedTo(email.trim().toLowerCase());
      setEmail(""); setName("");
      await refresh();
    } catch (e: any) {
      setError(e?.message || "Could not send invite");
    } finally { setBusy(false); }
  }

  async function removeStaff(id: string) {
    if (!confirm("Remove this staff login? They'll no longer be able to sign in.")) return;
    try { await staffApi.remove(id, "user"); await refresh(); }
    catch (e: any) { setError(e?.message || "Could not remove"); }
  }
  async function revokeInvite(id: string) {
    try { await staffApi.remove(id, "invite"); await refresh(); }
    catch (e: any) { setError(e?.message || "Could not revoke"); }
  }

  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <div className="panel-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2>Manage staff / admin</h2>
        <span className="muted small">{slotsLeft} of {cap} logins available</span>
      </div>

      {loading ? <p className="muted">Loading…</p> : (
        <>
          {/* Roster */}
          <div className="staff-list">
            {staff.map((s) => (
              <div key={s.id} className="staff-row">
                <div>
                  <strong>{s.name || s.email}</strong>
                  <span className="muted small"> · {s.email}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span className="pill">{ROLE_LABEL[s.role] || s.role}</span>
                  {s.role !== "owner" && s.id !== currentUserId && (
                    <button className="icon-button" aria-label={`Remove ${s.name || s.email}`} onClick={() => removeStaff(s.id)}>
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {invites.map((iv) => (
              <div key={iv.id} className="staff-row staff-row--pending">
                <div>
                  <strong>{iv.name || iv.email}</strong>
                  <span className="muted small"> · {iv.email} · invite pending</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span className="pill pill--muted">{ROLE_LABEL[iv.role] || iv.role}</span>
                  <button className="icon-button" aria-label={`Revoke invite for ${iv.email}`} onClick={() => revokeInvite(iv.id)}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Invite */}
          {slotsLeft > 0 ? (
            <form onSubmit={invite} className="stacked-form" style={{ marginTop: 14 }}>
              <div className="form-row-2">
                <label className="form-field"><span>Email</span>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="specialist@yourbusiness.com" required />
                </label>
                <label className="form-field"><span>Name (optional)</span>
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="First Last" />
                </label>
              </div>
              <label className="form-field"><span>Role</span>
                <select value={role} onChange={(e) => setRole(e.target.value)}>
                  {INVITE_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </label>
              {error && <p className="error-text" role="alert">{error}</p>}
              <button className="btn-primary" disabled={busy} style={{ alignSelf: "flex-start" }}>
                <UserPlus size={16} aria-hidden="true" /> {busy ? "Sending…" : "Send invite"}
              </button>
            </form>
          ) : (
            <p className="muted small" style={{ marginTop: 12 }}>
              You're at your {cap}-login limit. Remove a staff login or revoke a pending invite to add another.
            </p>
          )}

          {emailedTo && (
            <p className="muted small" style={{ marginTop: 10, color: "var(--ne-success)" }}>
              Invite emailed to {emailedTo}. They'll set their own password from the link.
            </p>
          )}
          {inviteLink && (
            <div className="staff-invite-link" style={{ marginTop: 10 }}>
              <p className="muted small" style={{ margin: "0 0 6px" }}>Email isn't configured yet — send them this invite link:</p>
              <div className="input-with-affix">
                <input value={inviteLink} readOnly onFocus={(e) => e.currentTarget.select()} />
                <button type="button" className="input-affix-button" aria-label="Copy invite link"
                  onClick={() => navigator.clipboard?.writeText(inviteLink)}>
                  <Copy size={15} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
